<template>
	<div>
		<!-- Overview Mode -->
		<twilio-overview-panel
			v-show="viewMode === 'overview'"
			:embedded="embedded"
			@select-organization="onOverviewSelectOrganization"
			@go-to-detail="goToDetail"
		/>

		<!-- Detail Mode -->
		<v-card
			v-show="viewMode === 'detail'"
			:class="$vuetify.breakpoint.xs ? 'ma-1' : 'ma-5'"
			flat
			:loading="loading"
		>
			<v-card-subtitle class="pb-1 d-flex align-center" style="min-width: 0">
				<v-btn
					icon
					small
					class="mr-2 flex-shrink-0"
					style="margin-top: -4px"
					@click="backToOverview"
				>
					<v-icon>mdi-arrow-left</v-icon>
				</v-btn>
				<v-autocomplete
					ref="orgAutocomplete"
					v-model="selectedOrganizationId"
					:items="organizations"
					item-text="displayName"
					item-value="id"
					:label="$t('twilio-debug-select-org')"
					dense
					outlined
					hide-details
					:loading="loadingOrgs"
					class="mb-2 org-autocomplete"
					style="min-width: 0; flex: 1 1 0"
					clearable
					:filter="filterOrganization"
					@change="onOrganizationChange"
				>
					<template #item="{ item }">
						<v-list-item-content style="overflow: hidden">
							<v-list-item-title style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis">
								{{ item.displayName }}
								<span class="text-caption grey--text">— {{ item.id }}</span>
							</v-list-item-title>
						</v-list-item-content>
					</template>
					<template #selection="{ item }">
						<div class="org-selection-text">
							{{ item.displayName }}
							<span class="text-caption grey--text">— {{ item.id }}</span>
						</div>
					</template>
				</v-autocomplete>
			</v-card-subtitle>

			<v-card-subtitle class="d-flex align-center flex-wrap pt-0">
				<div class="d-flex align-center mr-4">
					<v-icon
						:color="healthColor"
						class="mr-2"
						small
					>
						{{ healthIcon }}
					</v-icon>
					<span :class="$vuetify.breakpoint.xs ? 'text-body-2' : 'text-body-1'">
						<strong>{{ $t("twilio-debug-health") }}:</strong>
						{{ healthLabel }}
					</span>
				</div>
				<div v-if="debugData.sipDomain" class="text-caption grey--text" :class="{ 'ml-6': $vuetify.breakpoint.xs }">
					{{ debugData.sipDomain }}
				</div>
			</v-card-subtitle>

			<v-divider />

			<!-- Error message -->
			<v-alert
				v-if="errorMessage"
				type="error"
				dense
				text
				class="ma-4"
			>
				{{ errorMessage }}
			</v-alert>

			<v-card-text v-if="!errorMessage" :class="$vuetify.breakpoint.xs ? 'pa-1' : 'pa-4'" :style="twilioContentStyle">
				<!-- Alerts Section -->
				<div class="text-subtitle-1 font-weight-bold mb-2" :class="{ 'px-2': $vuetify.breakpoint.xs }">
					{{ $t("twilio-debug-alerts") }}
				</div>

				<v-tabs v-model="alertTab" dense class="twilio-debug-tabs" :show-arrows="$vuetify.breakpoint.xs">
					<v-tab v-for="(range, key) in alertRanges" :key="key" :class="{ 'px-2 tab-xs': $vuetify.breakpoint.xs }">
						<span :class="{ 'text-caption': $vuetify.breakpoint.xs }">{{ $t(`twilio-debug-last-${range}`) }}</span>
						<v-chip
							x-small
							:color="getAlerts(key).total > 0 ? 'red' : 'green'"
							dark
							class="ml-2"
						>
							{{ getAlerts(key).total || 0 }}
						</v-chip>
					</v-tab>
				</v-tabs>

				<v-tabs-items v-model="alertTab">
					<v-tab-item v-for="(range, key) in alertRanges" :key="key">
						<div :class="$vuetify.breakpoint.xs ? 'pa-1' : 'pa-3'">
							<!-- Mobile card layout -->
							<template v-if="$vuetify.breakpoint.xs && getAlerts(key).byErrorCode && getAlerts(key).byErrorCode.length">
								<v-card
									v-for="err in getAlerts(key).byErrorCode"
									:key="err.errorCode"
									:data-error-code="err.errorCode"
									outlined
									class="mb-1 error-card-mobile"
									:class="{ 'error-card-critical': isCriticalError(err), 'error-card-new': isNewError(err.errorCode), 'error-card-updated': isUpdatedError(err.errorCode) }"
								>
									<v-card-text class="pa-2">
										<!-- Header: error code + log level + count -->
										<div class="d-flex align-center justify-space-between">
											<div class="d-flex align-center">
												<a
													:href="`https://www.twilio.com/docs/api/errors/${err.errorCode}`"
													target="_blank"
													rel="noopener"
													class="error-code-link font-weight-bold text-body-2"
												>
													{{ err.errorCode }}
													<v-icon x-small class="ml-1">mdi-open-in-new</v-icon>
												</a>
												<v-chip
													v-if="err.logLevel"
													x-small
													:color="err.logLevel === 'ERROR' ? 'red' : err.logLevel === 'WARNING' ? 'orange' : 'grey'"
													dark
													class="ml-1"
												>
													{{ err.logLevel }}
												</v-chip>
											</div>
											<div class="d-flex align-center">
												<span class="text-caption grey--text mr-2">{{ formatDate(err.lastOccurrence) }}</span>
												<v-chip
													x-small
													:color="isCriticalError(err) ? 'red' : 'orange'"
													dark
												>
													{{ err.count }}
												</v-chip>
											</div>
										</div>

										<!-- Description -->
										<div v-if="err.description" class="text-caption mb-1 mt-1 mobile-description">
											{{ err.description }}
										</div>

										<!-- Sparkline + Affected users row -->
										<div class="d-flex align-center mt-1" style="gap: 8px">
											<v-tooltip v-if="getDistribution(err).some(v => v > 0)" bottom>
												<template #activator="{ on, attrs }">
													<div
														class="sparkline-container sparkline-container-mobile"
														v-bind="attrs"
														v-on="on"
														@mousemove="onSparklineMouseMove($event, key, err)"
														@mouseleave="onSparklineMouseLeave"
													>
														<v-sparkline
															:key="sparklineKey(err)"
															:value="[...getDistribution(err)]"
															:gradient="sparklineGradient(err)"
															:smooth="10"
															:padding="2"
															:line-width="2"
															:fill="true"
															height="24"
															width="100"
															type="bar"
														/>
													</div>
												</template>
												<span>{{ sparklineTooltip(key) }}</span>
											</v-tooltip>

											<div v-if="err.affectedUsers && err.affectedUsers.length" class="d-flex align-center flex-wrap" style="gap: 2px">
												<v-chip
													v-for="user in err.affectedUsers"
													:key="user.username"
													x-small
													:outlined="selectedUserFilter[err.errorCode] !== user.username"
													:color="selectedUserFilter[err.errorCode] === user.username ? 'primary' : undefined"
													:dark="selectedUserFilter[err.errorCode] === user.username"
													:class="{ 'user-chip-clickable': !!err.distributionByUser }"
													@click="toggleUserFilter(err, user.username)"
												>
													{{ resolveDisplayName(user.username) }}
													<span v-if="user.count > 1" class="ml-1 font-weight-bold">({{ user.count }})</span>
												</v-chip>
											</div>
										</div>

										<!-- Expandable causes/solutions -->
										<v-expansion-panels
											v-if="err.causes || err.solutions"
											flat
											accordion
											class="error-details-panels mt-1"
										>
											<v-expansion-panel v-if="err.causes">
												<v-expansion-panel-header class="pa-0 error-panel-header">
													<span class="text-caption grey--text">{{ $t("twilio-debug-causes") }}</span>
												</v-expansion-panel-header>
												<v-expansion-panel-content class="pa-0">
													<!-- eslint-disable-next-line vue/no-v-html -->
													<div class="text-caption" v-html="formatMarkdown(err.causes)" />
												</v-expansion-panel-content>
											</v-expansion-panel>
											<v-expansion-panel v-if="err.solutions">
												<v-expansion-panel-header class="pa-0 error-panel-header">
													<span class="text-caption grey--text">{{ $t("twilio-debug-solutions") }}</span>
												</v-expansion-panel-header>
												<v-expansion-panel-content class="pa-0">
													<!-- eslint-disable-next-line vue/no-v-html -->
													<div class="text-caption" v-html="formatMarkdown(err.solutions)" />
												</v-expansion-panel-content>
											</v-expansion-panel>
										</v-expansion-panels>
									</v-card-text>
								</v-card>
							</template>

							<!-- Desktop table layout -->
							<v-simple-table
								v-if="!$vuetify.breakpoint.xs && getAlerts(key).byErrorCode && getAlerts(key).byErrorCode.length"
								dense
							>
								<template #default>
									<thead>
										<tr>
											<th>{{ $t("twilio-debug-error-code") }}</th>
											<th>{{ $t("twilio-debug-message") }}</th>
											<th class="text-center">
												{{ $t("twilio-debug-count") }}
											</th>
											<th>{{ $t("twilio-debug-distribution") }}</th>
											<th>{{ $t("twilio-debug-affected-users") }}</th>
											<th>{{ $t("twilio-debug-last-occurrence") }}</th>
										</tr>
									</thead>
									<tbody>
										<tr
											v-for="err in getAlerts(key).byErrorCode"
											:key="err.errorCode"
											:data-error-code="err.errorCode"
											:class="{ 'error-row': isCriticalError(err), 'error-row-new': isNewError(err.errorCode), 'error-row-updated': isUpdatedError(err.errorCode) }"
										>
											<td>
												<a
													:href="`https://www.twilio.com/docs/api/errors/${err.errorCode}`"
													target="_blank"
													rel="noopener"
													class="error-code-link"
												>
													{{ err.errorCode }}
													<v-icon x-small class="ml-1">mdi-open-in-new</v-icon>
												</a>
												<div v-if="err.logLevel" class="mt-1">
													<v-chip
														x-small
														:color="err.logLevel === 'ERROR' ? 'red' : err.logLevel === 'WARNING' ? 'orange' : 'grey'"
														dark
													>
														{{ err.logLevel }}
													</v-chip>
												</div>
											</td>
											<td class="error-description-cell">
												<div v-if="err.description" class="text-body-2 font-weight-medium">
													{{ err.description }}
												</div>
												<v-expansion-panels
													v-if="err.causes || err.solutions"
													flat
													accordion
													class="error-details-panels mt-1"
												>
													<v-expansion-panel v-if="err.causes">
														<v-expansion-panel-header class="pa-0 error-panel-header">
															<span class="text-caption grey--text">{{ $t("twilio-debug-causes") }}</span>
														</v-expansion-panel-header>
														<v-expansion-panel-content class="pa-0">
															<!-- eslint-disable-next-line vue/no-v-html -->
															<div class="text-caption" v-html="formatMarkdown(err.causes)" />
														</v-expansion-panel-content>
													</v-expansion-panel>
													<v-expansion-panel v-if="err.solutions">
														<v-expansion-panel-header class="pa-0 error-panel-header">
															<span class="text-caption grey--text">{{ $t("twilio-debug-solutions") }}</span>
														</v-expansion-panel-header>
														<v-expansion-panel-content class="pa-0">
															<!-- eslint-disable-next-line vue/no-v-html -->
															<div class="text-caption" v-html="formatMarkdown(err.solutions)" />
														</v-expansion-panel-content>
													</v-expansion-panel>
												</v-expansion-panels>
												<span v-if="!err.description && !err.causes && !err.solutions" class="grey--text text-caption">—</span>
											</td>
											<td class="text-center">
												<v-chip
													x-small
													:color="isCriticalError(err) ? 'red' : 'orange'"
													dark
												>
													{{ err.count }}
												</v-chip>
											</td>
											<td class="sparkline-cell">
												<v-tooltip v-if="getDistribution(err).some(v => v > 0)" bottom>
													<template #activator="{ on, attrs }">
														<div class="sparkline-container" v-bind="attrs" v-on="on">
															<v-sparkline
																:key="sparklineKey(err)"
																:value="[...getDistribution(err)]"
																:gradient="sparklineGradient(err)"
																:smooth="10"
																:padding="4"
																:line-width="2"
																:fill="true"
																height="32"
																width="120"
																type="bar"
															/>
														</div>
													</template>
													<span>{{ sparklineTooltip(key) }}</span>
												</v-tooltip>
												<span v-else class="grey--text text-caption">—</span>
											</td>
											<td>
												<template v-if="err.affectedUsers && err.affectedUsers.length">
													<v-chip
														v-for="user in err.affectedUsers"
														:key="user.username"
														x-small
														:outlined="selectedUserFilter[err.errorCode] !== user.username"
														:color="selectedUserFilter[err.errorCode] === user.username ? 'primary' : undefined"
														:dark="selectedUserFilter[err.errorCode] === user.username"
														class="mr-1 mb-1"
														:class="{ 'user-chip-clickable': !!err.distributionByUser }"
														@click="toggleUserFilter(err, user.username)"
													>
														{{ resolveDisplayName(user.username) }}
														<span v-if="user.count > 1" class="ml-1 font-weight-bold">({{ user.count }})</span>
													</v-chip>
												</template>
												<span v-else class="grey--text text-caption">—</span>
											</td>
											<td class="text-caption">
												{{ formatDate(err.lastOccurrence) }}
											</td>
										</tr>
									</tbody>
								</template>
							</v-simple-table>

							<div v-if="!getAlerts(key).byErrorCode || !getAlerts(key).byErrorCode.length" class="text-body-2 grey--text text-center py-3">
								{{ $t("twilio-debug-no-alerts") }}
							</div>
						</div>
					</v-tab-item>
				</v-tabs-items>
			</v-card-text>
		</v-card>
	</div>
</template>

<script>
import TwilioOverviewPanel from "./TwilioOverviewPanel.vue";

const TRACKED_RANGES = ["last1h", "last24h", "last72h", "last30d"];

export default {
	name: "TwilioDebuggingPanel",

	components: {
		TwilioOverviewPanel
	},

	props: {
		embedded: {
			type: Boolean,
			default: false
		}
	},

	data() {
		return {
			viewMode: "detail", // "overview" or "detail"
			loading: false,
			loadingOrgs: false,
			errorMessage: "",
			alertTab: 0,
			organizations: [],
			selectedOrganizationId: null,
			debugData: {
				organizationId: "",
				sipDomain: "",
				overallHealth: "ok",
				alerts: {
					last1h: { total: 0, byErrorCode: []},
					last24h: { total: 0, byErrorCode: []},
					last72h: { total: 0, byErrorCode: []},
					last30d: { total: 0, byErrorCode: []}
				}
			},
			alertRanges: {
				last1h: "1h",
				last24h: "24h",
				last72h: "72h",
				last30d: "30d"
			},
			selectedUserFilter: {},
			newErrorCodes: new Set(),
			newErrorClearTimeout: null,
			updatedErrorCodes: new Set(),
			updatedErrorClearTimeout: null,
			previousDebugData: null
		};
	},

	computed: {
		currentOrganizationId() {
			return this.$store.getters["login/profile"]?.clinic || null;
		},

		twilioContentStyle() {
			if (this.$vuetify.breakpoint.xs) {
				return { overflowY: "auto", flex: "1" };
			}
			return { maxHeight: "60vh", overflowY: "auto" };
		},

		healthColor() {
			switch (this.debugData.overallHealth) {
				case "error": return "red";
				case "warning": return "orange";
				default: return "green";
			}
		},

		healthIcon() {
			switch (this.debugData.overallHealth) {
				case "error": return "mdi-alert-circle";
				case "warning": return "mdi-alert";
				default: return "mdi-check-circle";
			}
		},

		healthLabel() {
			switch (this.debugData.overallHealth) {
				case "error": return this.$t("twilio-debug-health-error");
				case "warning": return this.$t("twilio-debug-health-warning");
				default: return this.$t("twilio-debug-health-ok");
			}
		}
	},

	async created() {
		await this.fetchOrganizations();
		if (this.currentOrganizationId) {
			this.selectedOrganizationId = this.currentOrganizationId;
			this.$nextTick(() => {
				this.fetchDebugData();
			});
		}
	},

	methods: {
		/**
		 * Called from overview panel when an organization is selected
		 */
		onOverviewSelectOrganization(orgId) {
			this.resetDetailState();
			this.selectedOrganizationId = orgId;
			this.viewMode = "detail";
			this.$nextTick(() => {
				this.fetchDebugData();
			});
		},

		/**
		 * Return to overview mode
		 */
		backToOverview() {
			this.viewMode = "overview";
			this.selectedOrganizationId = null;
			this.errorMessage = "";
			this.selectedUserFilter = {};
		},

		goToDetail() {
			this.resetDetailState();
			if (this.currentOrganizationId) {
				this.selectedOrganizationId = this.currentOrganizationId;
			}
			this.viewMode = "detail";
			this.$nextTick(() => {
				if (this.selectedOrganizationId) {
					this.fetchDebugData();
				}
			});
		},

		resetDetailState() {
			this.errorMessage = "";
			this.selectedUserFilter = {};
			this.alertTab = 0;
			this.previousDebugData = null;
			this.newErrorCodes = new Set();
			this.updatedErrorCodes = new Set();
			if (this.newErrorClearTimeout) {
				clearTimeout(this.newErrorClearTimeout);
				this.newErrorClearTimeout = null;
			}
			if (this.updatedErrorClearTimeout) {
				clearTimeout(this.updatedErrorClearTimeout);
				this.updatedErrorClearTimeout = null;
			}
			this.debugData = {
				organizationId: "",
				sipDomain: "",
				overallHealth: "ok",
				alerts: {
					last1h: { total: 0, byErrorCode: []},
					last24h: { total: 0, byErrorCode: []},
					last72h: { total: 0, byErrorCode: []},
					last30d: { total: 0, byErrorCode: []}
				}
			};
		},

		async fetchOrganizations() {
			this.loadingOrgs = true;
			try {
				const { data } = await this.axios.get("/devops/organizations");

				this.organizations = data
					.map(org => ({
						id: org.id,
						displayName: this.organizationName(org.name)
					}))
					.sort((a, b) => a.displayName.localeCompare(b.displayName));
			} catch (err) {
				console.error("Error fetching organizations:", err);
			} finally {
				this.loadingOrgs = false;
			}
		},

		onOrganizationChange() {
			// Reset to current org when cleared
			if (!this.selectedOrganizationId && this.currentOrganizationId) {
				this.selectedOrganizationId = this.currentOrganizationId;
			}
			// Blur the autocomplete to collapse the search input
			this.$nextTick(() => {
				this.$refs.orgAutocomplete?.blur();
			});
			this.selectedUserFilter = {};
			// Clear historical error tracking to prevent false blinking when switching organizations
			this.debugData = {
				organizationId: "",
				sipDomain: "",
				overallHealth: "ok",
				alerts: {
					last1h: { total: 0, byErrorCode: []},
					last24h: { total: 0, byErrorCode: []},
					last72h: { total: 0, byErrorCode: []},
					last30d: { total: 0, byErrorCode: []}
				}
			};
			if (this.newErrorClearTimeout) {
				clearTimeout(this.newErrorClearTimeout);
				this.newErrorClearTimeout = null;
			}
			if (this.updatedErrorClearTimeout) {
				clearTimeout(this.updatedErrorClearTimeout);
				this.updatedErrorClearTimeout = null;
			}
			this.newErrorCodes.clear();
			this.updatedErrorCodes.clear();
			this.previousDebugData = null;
			this.fetchDebugData();
		},

		async fetchDebugData(options) {
			const silent = options?.silent || false;
			let success = true;

			// Skip silent request if a non-silent fetch is already in progress
			if (silent && this.loading) {
				return { success: false };
			}

			if (!silent) {
				this.loading = true;
				this.errorMessage = "";
			}

			// Capture organization ID at time of request to detect race conditions
			const requestOrganizationId = this.selectedOrganizationId;

			try {
				const params = this.selectedOrganizationId
					? { id: this.selectedOrganizationId }
					: {};
				const { data } = await this.axios.get("/devops/twilio/debug", { params });

				// Ignore stale responses from switched organization (race condition)
				if (requestOrganizationId !== this.selectedOrganizationId) {
					return { success: false };
				}

				// Track new and updated errors BEFORE updating debugData (even on silent refresh)
				// Only run detection if we have previous data to compare against (skip on initial load)
				if (this.debugData.alerts) {
					const previousErrorCodes = this.getAllErrorCodes(this.debugData);

					// Only detect changes if we had errors before (not on initial load)
					if (previousErrorCodes.size > 0) {
						const currentErrorCodes = this.getAllErrorCodes(data);
						const newCodes = new Set(
							[...currentErrorCodes].filter(code => !previousErrorCodes.has(code))
						);

						if (newCodes.size > 0) {
							this.newErrorCodes = newCodes;
							// Clear the "new" indicator after animation completes (3 seconds for blink)
							if (this.newErrorClearTimeout) {
								clearTimeout(this.newErrorClearTimeout);
							}
							this.newErrorClearTimeout = setTimeout(() => {
								this.newErrorCodes.clear();
								this.newErrorClearTimeout = null;
							}, 3000);
						}

						// Track updated errors (errors with more recent lastOccurrence)
						const updatedCodes = new Set();

						for (const range of TRACKED_RANGES) {
							const prevErrors = this.debugData.alerts?.[range]?.byErrorCode || [];
							const currErrors = data.alerts?.[range]?.byErrorCode || [];

							const prevMap = new Map(prevErrors.map(e => [e.errorCode, e]));

							for (const currErr of currErrors) {
								const prevErr = prevMap.get(currErr.errorCode);

								if (prevErr && currErr.lastOccurrence && prevErr.lastOccurrence) {
									const currTime = new Date(currErr.lastOccurrence).getTime();
									const prevTime = new Date(prevErr.lastOccurrence).getTime();

									if (currTime > prevTime) {
										updatedCodes.add(currErr.errorCode);
									}
								}
							}
						}

						if (updatedCodes.size > 0) {
							this.updatedErrorCodes = updatedCodes;

							if (this.updatedErrorClearTimeout) {
								clearTimeout(this.updatedErrorClearTimeout);
							}
							this.updatedErrorClearTimeout = setTimeout(() => {
								this.updatedErrorCodes.clear();
								this.updatedErrorClearTimeout = null;
							}, 5000);
						}
					}
				}

				this.debugData = data;

				if (data.error) {
					if (!silent) this.errorMessage = data.error;
					success = false;
				}

				// Auto-select the first tab that has errors
				if (!silent) this.alertTab = this.getDefaultAlertTab();
			} catch (err) {
				console.error("Error fetching Twilio debug data:", err);
				success = false;
				if (!silent) this.errorMessage = this.$t("twilio-debug-fetch-error");
			} finally {
				if (!silent) {
					this.loading = false;
				}
			}

			return { success };
		},

		getAlerts(range) {
			return this.debugData.alerts?.[range] || { total: 0, byErrorCode: []};
		},

		getAllErrorCodes(debugData) {
			const codes = new Set();

			if (debugData.alerts) {
				TRACKED_RANGES.forEach(range => {
					const alerts = debugData.alerts[range];

					if (alerts && alerts.byErrorCode) {
						alerts.byErrorCode.forEach(err => codes.add(err.errorCode));
					}
				});
			}
			return codes;
		},

		isNewError(errorCode) {
			const result = this.newErrorCodes.has(errorCode);

			if (result) {
				// Trigger blinking effect on next tick
				this.$nextTick(() => {
					this.triggerBlink(errorCode);
				});
			}
			return result;
		},

		isUpdatedError(errorCode) {
			const result = this.updatedErrorCodes.has(errorCode);

			if (result) {
				// Trigger blinking effect on next tick
				this.$nextTick(() => {
					this.triggerBlink(errorCode);
				});
			}
			return result;
		},

		triggerBlink(errorCode) {
			// Find the row/card element for this error code
			const elements = document.querySelectorAll(`[data-error-code="${errorCode}"]`);

			// Find the error object to check if it's critical
			let error = null;

			for (const range of TRACKED_RANGES) {
				const found = this.debugData.alerts?.[range]?.byErrorCode?.find(e => e.errorCode === errorCode);

				if (found) {
					error = found;
					break;
				}
			}

			const lightRed = "#FF6B6B";
			const transparent = "transparent";
			const isCritical = error ? this.isCriticalError(error) : false;
			const finalColor = isCritical ? "rgba(244, 67, 54, 0.08)" : transparent;

			elements.forEach((el) => {
				// Blink frequently for 5 seconds
				let blinkCount = 0;
				const blinkInterval = setInterval(() => {
					const color = blinkCount % 2 === 0 ? lightRed : transparent;

					el.style.setProperty("background-color", color, "important");

					if (blinkCount >= 59) { // ~60 blinks = 30 on/off cycles over 5 seconds
						clearInterval(blinkInterval);
						el.style.setProperty("background-color", finalColor, "important");
						return;
					}
					blinkCount++;
				}, 83); // 83ms per blink
			});
		},

		getDefaultAlertTab() {
			const alerts = this.debugData?.alerts;

			if (!alerts) return 0;
			if (alerts.last1h?.total > 0) return 0;
			if (alerts.last24h?.total > 0) return 1;
			if (alerts.last72h?.total > 0) return 2;
			if (alerts.last30d?.total > 0) return 3;
			return 0;
		},

		isCriticalError(err) {
			if (err.errorCode === "32011" || err.errorCode === "32007") return true;
			if (!err.affectedUsers || !err.affectedUsers.length) return false;
			return err.errorCode === "32009";
		},

		sparklineGradient(err) {
			if (this.isCriticalError(err)) {
				return ["#ef9a9a", "#e53935"];
			}
			return ["#ffcc80", "#fb8c00"];
		},

		getDistribution(err) {
			const selected = this.selectedUserFilter[err.errorCode];

			if (selected && err.distributionByUser?.[selected]) {
				return err.distributionByUser[selected];
			}
			return err.distribution || [];
		},

		sparklineKey(err) {
			const selected = this.selectedUserFilter[err.errorCode] || "all";
			const dist = this.getDistribution(err);

			return `${err.errorCode}-${selected}-${dist.join(",")}`;
		},

		sparklineTooltip(rangeKey) {
			const now = new Date();
			let start;
			let bucketLabel;

			switch (rangeKey) {
				case "last1h":
					start = new Date(now.getTime() - 60 * 60 * 1000);
					bucketLabel = this.$t("twilio-debug-per-2-min");
					break;
				case "last24h":
					start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
					bucketLabel = this.$t("twilio-debug-per-hour");
					break;
				case "last72h":
					start = new Date(now.getTime() - 72 * 60 * 60 * 1000);
					bucketLabel = this.$t("twilio-debug-per-3h");
					break;
				case "last30d":
					start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
					bucketLabel = this.$t("twilio-debug-per-day");
					break;
				default:
					return "";
			}

			const fmt = (d) => d.toLocaleString("en-CA", {
				timeZone: "America/New_York",
				month: "short",
				day: "numeric",
				hour: "2-digit",
				minute: "2-digit",
				hour12: false
			});

			return `${fmt(start)} → ${fmt(now)} EST (${bucketLabel})`;
		},

		toggleUserFilter(err, username) {
			if (!err.distributionByUser) return;
			if (this.selectedUserFilter[err.errorCode] === username) {
				this.$set(this.selectedUserFilter, err.errorCode, null);
			} else {
				this.$set(this.selectedUserFilter, err.errorCode, username);
			}
		},

		resolveDisplayName(username) {
			return username;
		},

		formatDate(dateStr) {
			if (!dateStr) return "—";
			try {
				const d = new Date(dateStr);
				const formatted = d.toLocaleString("en-CA", {
					timeZone: "America/New_York",
					year: "numeric",
					month: "short",
					day: "numeric",
					hour: "2-digit",
					minute: "2-digit",
					second: "2-digit",
					hour12: false
				});

				return `${formatted} (EST)`;
			} catch {
				return dateStr;
			}
		},

		formatMarkdown(text) {
			if (!text) return "";
			return text
				.replace(/\*\s/g, "• ")
				.replace(/\[([^\]]+)\]\(([^)]+)\)/g, "<a href=\"https://www.twilio.com$2\" target=\"_blank\" rel=\"noopener\">$1</a>")
				.replace(/\n/g, "<br>");
		},

		organizationName(name) {
			if (!name) return "";
			const [locale] = Object.keys(name);

			return name[locale];
		},

		normalizeText(text) {
			return text
				.toLowerCase()
				.normalize("NFD")
				.replace(/[\u0300-\u036f]/g, "");
		},

		filterOrganization(item, queryText) {
			const search = this.normalizeText(queryText);
			const name = this.normalizeText(item.displayName || "");
			const id = this.normalizeText(item.id || "");

			return name.includes(search) || id.includes(search);
		}
	}
};
</script>

<style scoped>
	.error-row,
	.v-data-table tbody tr.error-row:hover {
		background-color: rgba(244, 67, 54, 0.08) !important;
	}

	.error-code-link {
		text-decoration: none;
		color: inherit;
		font-weight: 500;
	}

	.error-code-link:hover {
		text-decoration: underline;
		color: #1976d2;
	}

	.error-description-cell {
		max-width: 320px;
		min-width: 200px;
	}

	.sparkline-cell {
		width: 140px;
		min-width: 120px;
		padding: 4px 8px !important;
	}

	.sparkline-container {
		display: flex;
		align-items: center;
		border: 1px solid rgba(0, 0, 0, 0.12);
		border-radius: 4px;
		background-color: rgba(0, 0, 0, 0.02);
		padding: 2px;
	}

	.user-chip-clickable {
		cursor: pointer;
	}

	.error-details-panels {
		background: transparent !important;
	}

	.error-details-panels .v-expansion-panel {
		background: transparent !important;
	}

	.error-details-panels .v-expansion-panel::before {
		box-shadow: none !important;
	}

	.error-panel-header {
		min-height: 24px !important;
	}

	.error-card-mobile {
		border-left: 3px solid #e0e0e0;
	}

	.org-selection-text {
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		max-width: calc(100% - 8px);
	}

	.error-card-mobile.error-card-critical {
		border-left-color: #f44336;
		background-color: rgba(244, 67, 54, 0.04);
	}

	.mobile-description {
		line-height: 1.2;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}

	.sparkline-container-mobile {
		display: flex;
		align-items: center;
		border: 1px solid rgba(0, 0, 0, 0.12);
		border-radius: 4px;
		background-color: rgba(0, 0, 0, 0.02);
		padding: 1px 2px;
		flex-shrink: 0;
		width: 100px;
		height: 28px;
	}

	.sparkline-container-mobile svg {
		width: 100% !important;
		height: 100% !important;
		display: block;
	}

	@media (max-width: 599px) {
		.v-card {
			display: flex;
			flex-direction: column;
			height: 100%;
		}
	}
</style>

<style>
	.v-simple-table tbody tr.error-row,
	.v-simple-table tbody tr.error-row:hover {
		background-color: rgba(244, 67, 54, 0.08) !important;
	}

	.twilio-debug-tabs .v-tab--active {
		font-weight: 700;
		background-color: rgba(25, 118, 210, 0.08);
		border-radius: 4px 4px 0 0;
	}

	.twilio-debug-tabs .tab-xs {
		min-width: 60px !important;
		padding: 0 8px !important;
		font-size: 11px !important;
		letter-spacing: 0 !important;
	}

	.org-autocomplete .v-select__selections {
		flex-wrap: nowrap !important;
		overflow: hidden;
	}

	.org-autocomplete .v-select__selections input {
		max-width: 0;
		min-width: 0 !important;
		padding: 0 !important;
		width: 0;
	}

	.org-autocomplete.v-input--is-focused .v-select__selections input {
		max-width: 100%;
		min-width: 64px !important;
		width: 100%;
	}

	.org-autocomplete.v-input--is-focused .org-selection-text {
		display: none;
	}
</style>
